/* PDP-2026 gallery. Standalone — no css-slider, no section-main-product.js.
   Scrolling and swipe are CSS scroll-snap; this only reflects position back into
   the rail/dots, jumps on variant change, and stops video that scrolled away. */
if (!customElements.get("pdp-gallery")) {
  class PdpGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector("[data-gal-track]");
      if (!this.track) return;

      this.rail = this.querySelector("[data-gal-rail]");
      this.buttons = Array.from(
        this.querySelectorAll("[data-gal-thumb], [data-gal-dot]"),
      );
      this._refresh();

      /* Flag each slide once its image has actually decoded. CSS shows the
         brand-mark placeholder (and suppresses the alt text) until then, and
         must stop once the photo is there — some product shots are transparent
         PNGs and would otherwise keep the mark visible behind them.
         Queried off the track, not this.slides, because _refresh() drops the
         slides that are mobile-hidden and those still need the flag. */
      this.track.querySelectorAll(".pdp-gal__slide").forEach((slide) => {
        const img = slide.querySelector("img");
        if (!img) return ((slide.dataset.loaded = ""), undefined);
        if (img.complete && img.naturalWidth) slide.dataset.loaded = "";
        else
          img.addEventListener("load", () => (slide.dataset.loaded = ""), {
            once: true,
          });
      });

      this.buttons.forEach((b) =>
        b.addEventListener("click", () => {
          const pos = this.pos.get(b.dataset.index);
          if (pos != null) this.goTo(pos);
        }),
      );
      /* Mobile hides the custom.gallery slides (they live in the sheet instead),
         so the visible set changes at the breakpoint — recount, or the dots and
         the arrows' end-detection describe slides nobody can reach. */
      this._mq = window.matchMedia(
        "(min-width: 1025px), (min-width: 769px) and (orientation: landscape)",
      );
      this._mq.addEventListener("change", () => {
        this._refresh();
        this._observe();
        this._setActive(Math.min(this.current ?? 0, this.slides.length - 1));
      });

      this.prev = this.querySelector("[data-gal-prev]");
      this.next = this.querySelector("[data-gal-next]");
      this.prev?.addEventListener("click", () => this.step(-1));
      this.next?.addEventListener("click", () => this.step(1));

      /* Keyboard: the track is focusable (tabindex in the snippet). Arrow keys
         step one slide rather than the browser's ~40px keyboard scroll;
         Home/End jump to the ends. */
      this.track.addEventListener("keydown", (e) => {
        const to = {
          ArrowLeft: (this.current ?? 0) - 1,
          ArrowRight: (this.current ?? 0) + 1,
          Home: 0,
          End: this.slides.length - 1,
        }[e.key];
        if (to == null) return;
        e.preventDefault();
        this.goTo(Math.max(0, Math.min(to, this.slides.length - 1)));
      });

      /* Warm the slide you are about to ask for. Every slide past the first is
         loading="lazy" (the hero owns the connection at load time), so a click
         used to be followed by a blank frame while the full-size image
         downloaded. Hovering a thumb or an arrow is a reliable ~200ms of notice.

         Flipping loading from lazy to eager is what actually starts the fetch —
         per spec that is the trigger. Idempotent via _warmed, and pointerenter
         rather than mouseover so it fires once per element, not per child.
         focusin covers the keyboard path. Touch has no hover, but there the tap
         itself starts the load and nothing is lost. */
      this.buttons.forEach((b) => {
        const warmThis = () => this._warm(this.pos.get(b.dataset.index));
        b.addEventListener("pointerenter", warmThis, { passive: true });
        b.addEventListener("focusin", warmThis);
      });
      [
        [this.prev, -1],
        [this.next, 1],
      ].forEach(([btn, delta]) => {
        if (!btn) return;
        const warmStep = () => this._warm((this.current ?? 0) + delta);
        btn.addEventListener("pointerenter", warmStep, { passive: true });
        btn.addEventListener("focusin", warmStep);
      });

      /* No scrollend in every browser we support, so settle on a debounce. */
      let settle;
      this.track.addEventListener("scroll", () => {
        clearTimeout(settle);
        settle = setTimeout(() => this._settle(), 120);
      });

      this._observe();
      this._bindVariantChange();

      /* Jump to the selected variant's image without animating on first paint.
         "auto" rather than "smooth" so a deep link does not visibly slide. */
      /* Set the state up front rather than waiting for the observer: without
         IntersectionObserver support nothing else ever calls _setActive, so
         `current` stayed undefined and the arrows never got a disabled state. */
      /* data-start is a GLOBAL slide index from Liquid; translate it to a
         position in the visible set before using it. */
      const start = this.pos.get(this.dataset.start || "0") ?? 0;
      this._setActive(start);
      if (start > 0) this.goTo(start, "auto");
    }

    goTo(index, behavior = "smooth") {
      const slide = this.slides[index];
      if (!slide) return;
      this._target = index;
      /* offsetParent is the sticky .pdp-gallery-wrap, not the track, so
         slide.offsetLeft carries the rail's 91px offset. Subtracting the
         track's own offset gives a real scroll position — scroll-snap was
         hiding the discrepancy by correcting to the nearest slide. */
      const left = slide.offsetLeft - this.track.offsetLeft;
      /* Scrolling to where we already are fires NO scroll event, so the debounce
         would never clear _seeking and the rail reveal would stay dead for the
         rest of the page's life. Clicking the already-active thumb did exactly
         that. Settle immediately instead. */
      if (Math.abs(this.track.scrollLeft - left) < 1) {
        this._settle();
        return;
      }
      /* A smooth scroll crosses every slide in between, so the observer fires
         for each one. Hold the rail still until the track settles, or it chases
         each intermediate thumb and the whole column visibly jumps. */
      this._seeking = true;
      /* a clamped target (last slide, already at max scroll) can also produce no
         event — never let the flag outlive the scroll it was guarding */
      clearTimeout(this._seekFallback);
      this._seekFallback = setTimeout(() => this._settle(), 600);
      this.track.scrollTo({ left, behavior });
    }

    /* End of a programmatic scroll: release the guard, then apply the state for
       the slide we were actually heading to. */
    _settle() {
      clearTimeout(this._seekFallback);
      this._seeking = false;
      const target = this._target;
      this._target = null;
      this._setActive(target ?? this.current ?? 0);
      this._fromVariant = false;
    }

    step(delta) {
      this.goTo((this.current ?? 0) + delta);
    }

    /* Start the fetch for one slide's image, at most once. */
    _warm(index) {
      const img = this.slides?.[index]?.querySelector("img[loading='lazy']");
      if (!img || img._warmed) return;
      img._warmed = true;
      img.loading = "eager";
      img.fetchPriority = "high";
    }

    _setActive(index) {
      this.current = index;
      /* no wrap-around, so the ends have to say so rather than dead-click */
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === this.slides.length - 1;
      this.buttons.forEach((b) =>
        b.classList.toggle("active", this.pos.get(b.dataset.index) === index),
      );
      /* A video left playing off-screen keeps talking over the page. */
      this.slides.forEach((s, i) => {
        if (i !== index) s.querySelector("video")?.pause();
      });
      /* flag the active slide when it is a video, so the toggle can step out of
         the way of the browser's control bar */
      this.slides.forEach((s, i) =>
        s.toggleAttribute(
          "data-video-active",
          i === index && !!s.querySelector("video"),
        ),
      );
      this._revealThumb(index);
      this._syncVariant(index);
    }

    /* Moving the gallery moves the variant, the way css-slider's connectedIndex
       used to: a slide carries the colour/design value of the variant that owns
       its media, so clicking that thumbnail selects the colour. Skipped when the
       move CAME from a variant change, or the two would ping-pong. */
    _syncVariant(index) {
      const opt = this.dataset.syncOption;
      const value = this.slides[index]?.dataset.optValue;
      if (!opt || !value || this._fromVariant) return;
      const group = this.closest("product-page")?.querySelector(
        `.product-variant[data-name^="product-${opt}-"]`,
      );
      const input = group?.querySelector(
        `input.product-variant-value[value="${CSS.escape(value)}"]`,
      );
      if (!input || input.checked || input.disabled) return;
      input.checked = true;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    /* Scroll the rail itself rather than calling scrollIntoView, which would
       also scroll every ancestor — including the page — and yank the PDP
       around on variant change. Only the rail's own overflow axis moves.

       Nudges the thumb just inside view instead of centring it: centring moved
       the rail even when the thumb was already visible, so clicking one shifted
       it out from under the cursor. */
    _revealThumb(index) {
      const rail = this.rail;
      const thumb = rail?.querySelector(
        `[data-gal-thumb][data-index="${this.slides[index]?.dataset.index}"]`,
      );
      if (!rail || !thumb || this._seeking) return;
      const r = rail.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      const delta = (near, far) => (near < 0 ? near : far > 0 ? far : 0);
      const top = delta(t.top - r.top, t.bottom - r.bottom);
      const left = delta(t.left - r.left, t.right - r.right);
      if (!top && !left) return;
      rail.scrollBy({ top, left, behavior: "smooth" });
    }

    /* Visible slides only — hidden ones must not occupy an index, or every dot
       after them points at the wrong picture. */
    _refresh() {
      this.slides = Array.from(
        this.querySelectorAll("[data-gal-slide]"),
      ).filter((s) => getComputedStyle(s).display !== "none");
      this.pos = new Map(this.slides.map((s, i) => [s.dataset.index, i]));
    }

    _observe() {
      if (!("IntersectionObserver" in window)) return;
      this._io?.disconnect();
      this._io = new IntersectionObserver(
        (entries) => {
          /* Intermediate slides cross the threshold during a programmatic
             scroll; acting on them flickered the active dot and the arrows'
             disabled state through every index on the way. _settle() applies
             the final state instead. */
          if (this._seeking) return;
          entries.forEach((e) => {
            if (e.isIntersecting && e.intersectionRatio >= 0.6) {
              this._setActive(this.slides.indexOf(e.target));
            }
          });
        },
        { root: this.track, threshold: [0.6] },
      );
      this.slides.forEach((s) => this._io.observe(s));
    }

    /* VARIANT_CHANGE is dispatched on <product-variants> and does not bubble
       (component-product-form.js:45), so bind to that element directly. */
    _bindVariantChange() {
      const variants =
        this.closest("product-page")?.querySelector("product-variants");
      if (!variants) return;
      variants.addEventListener("VARIANT_CHANGE", (e) => {
        const id = e.target.currentVariant?.featured_media?.id;
        if (!id) return;
        const index = this.slides.findIndex(
          (s) => s.dataset.mediaId === String(id),
        );
        if (index < 0) return;
        /* a variant whose image is hidden on this breakpoint has no slide to
           jump to — leave the gallery where it is rather than guessing */
        this._fromVariant = true;
        this.goTo(index);
      });
    }

    disconnectedCallback() {
      this._io?.disconnect();
    }
  }
  customElements.define("pdp-gallery", PdpGallery);
}
